import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useEditSession, type EditSession, type EditSessionProfile } from '@/hooks/useEditSession';

type Step = 'phone' | 'otp' | 'picker';

interface VerifyPhoneDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const editPath = (p: EditSessionProfile) => `/upravit/${p.slug}`;

const VerifyPhoneDialog = ({ open, onOpenChange }: VerifyPhoneDialogProps) => {
  const navigate = useNavigate();
  const { session, saveSession } = useEditSession();
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [profiles, setProfiles] = useState<EditSessionProfile[]>([]);

  // Reset dialog when opened; pick right starting step based on session.
  useEffect(() => {
    if (!open) return;

    if (session) {
      if (session.profiles.length === 1) {
        onOpenChange(false);
        navigate(editPath(session.profiles[0]));
        return;
      }
      setProfiles(session.profiles);
      setStep('picker');
      return;
    }

    setStep('phone');
    setPhone('');
    setCode('');
    setProfiles([]);
  }, [open, session, navigate, onOpenChange]);

  const handleSendCode = async () => {
    if (!phone.trim()) {
      toast.error('Zadejte telefonní číslo');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('verify-phone-start', {
        body: { phone: phone.trim() },
      });
      if (error) {
        let body: { error?: string; retryAfterMs?: number } = {};
        try {
          body = await (error.context?.json?.() ?? Promise.resolve({}));
        } catch {
          /* ignore */
        }
        if (body.error === 'no_profile_for_phone') {
          toast.error('K tomuto číslu nemáme žádný profil. Pro úpravy nás prosím kontaktujte na e-mailu.');
        } else if (body.error === 'invalid_phone') {
          toast.error('Neplatný formát telefonního čísla.');
        } else if (body.error === 'too_soon') {
          const secs = Math.ceil((body.retryAfterMs ?? 60000) / 1000);
          toast.error(`Kód už byl nedávno odeslán. Zkuste to znovu za ${secs} s.`);
        } else {
          toast.error('Nepodařilo se odeslat ověřovací SMS. Zkuste to prosím znovu.');
        }
        return;
      }
      setStep('otp');
      toast.success('Kód byl odeslán SMS zprávou.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (submittedCode: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-phone-check', {
        body: { phone: phone.trim(), code: submittedCode },
      });
      if (error) {
        let body: { error?: string; attemptsRemaining?: number } = {};
        try {
          body = await (error.context?.json?.() ?? Promise.resolve({}));
        } catch {
          /* ignore */
        }
        if (body.error === 'verify_failed') {
          const remaining = body.attemptsRemaining;
          toast.error(
            remaining != null
              ? `Nesprávný kód. Zbývá pokusů: ${remaining}.`
              : 'Nesprávný kód, zkuste to prosím znovu.',
          );
          setCode('');
        } else if (body.error === 'code_expired_or_missing') {
          toast.error('Kód vypršel. Nechte si prosím poslat nový.');
          setCode('');
          setStep('phone');
        } else {
          toast.error('Ověření se nezdařilo. Zkuste to prosím znovu.');
        }
        return;
      }

      const next: EditSession = {
        sessionToken: data.sessionToken,
        expiresAt: data.expiresAt,
        profiles: data.profiles,
      };
      saveSession(next);

      if (next.profiles.length === 1) {
        onOpenChange(false);
        navigate(editPath(next.profiles[0]));
        return;
      }
      setProfiles(next.profiles);
      setStep('picker');
    } finally {
      setLoading(false);
    }
  };

  const selectProfile = (profile: EditSessionProfile) => {
    onOpenChange(false);
    navigate(editPath(profile));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl">
        {step === 'phone' && (
          <>
            <DialogHeader>
              <DialogTitle>Upravit profil</DialogTitle>
              <DialogDescription>
                Pro úpravu profilu zadejte telefonní číslo, které máte u svého stánku nebo aktivity uložené. Pošleme vám ověřovací kód SMSkou.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <Label htmlFor="verify-phone">Telefonní číslo</Label>
              <Input
                id="verify-phone"
                placeholder="+420 123 456 789"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !loading && handleSendCode()}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">
                Zrušit
              </Button>
              <Button
                onClick={handleSendCode}
                disabled={loading}
                className="bg-primary text-brand-dark hover:bg-primary/90 font-bold rounded-xl"
              >
                {loading ? 'Odesílám…' : 'Odeslat kód'}
              </Button>
            </div>
          </>
        )}

        {step === 'otp' && (
          <>
            <DialogHeader>
              <DialogTitle>Zadejte kód z SMS</DialogTitle>
              <DialogDescription>
                Poslali jsme kód na {phone}. Vyplňte ho prosím níže.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center py-4">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={(val) => {
                  setCode(val);
                  if (val.length === 6) handleVerifyCode(val);
                }}
                disabled={loading}
              >
                <InputOTPGroup>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <InputOTPSlot key={i} index={i} className="h-12 w-10" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <div className="flex justify-between gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setStep('phone')}
                disabled={loading}
                className="rounded-xl"
              >
                Zpět
              </Button>
              <Button
                onClick={() => handleVerifyCode(code)}
                disabled={loading || code.length !== 6}
                className="bg-primary text-brand-dark hover:bg-primary/90 font-bold rounded-xl"
              >
                {loading ? 'Ověřuji…' : 'Ověřit'}
              </Button>
            </div>
          </>
        )}

        {step === 'picker' && (
          <>
            <DialogHeader>
              <DialogTitle>Vyberte profil</DialogTitle>
              <DialogDescription>
                K vašemu telefonnímu číslu je přiřazeno více profilů. Vyberte, který chcete upravit.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-80 overflow-auto py-2">
              {profiles.map((p) => (
                <button
                  key={`${p.type}:${p.id}`}
                  type="button"
                  onClick={() => selectProfile(p)}
                  className={cn(
                    'w-full text-left p-4 rounded-xl border-2 border-slate-200 bg-white',
                    'hover:border-primary hover:bg-primary/5 transition-all',
                    'flex items-center gap-3',
                  )}
                >
                  <span className="material-symbols-outlined text-primary text-2xl">
                    {p.type === 'vendor' ? 'storefront' : 'play_circle'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-brand-dark truncate">{p.name}</div>
                    <div className="text-xs text-slate-500">
                      {p.type === 'vendor' ? 'Stánek' : 'Aktivita / řemeslo'}
                    </div>
                  </div>
                  <span className="material-symbols-outlined text-slate-400">chevron_right</span>
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VerifyPhoneDialog;
